import React from 'react';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';
import {
    Box,
    Card,
    CardContent,
    CardHeader, Grid, IconButton, Link,
    Table, TableBody, TableCell,
    TableContainer, TableHead,
    TableRow, Typography
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import Currency from 'components/formaters/Currency';
import Date from 'components/formaters/Date';
import { InvoiceableModel, InvoiceModel, InvoiceStatus } from 'models/InvoiceModel';
import { InvoiceRenderLink } from 'modules/invoices/common/Components';


type InvoiceProps = {
    invoice: InvoiceModel,
}

const InvoiceView = ({invoice}: InvoiceProps) => {
    const {t} = useTranslation();

    let Items: ({items}: FreightInvoiceableTableProps) => JSX.Element;

    if (isMobile) {
        Items = invoice.type === 'freight' ?
            FreightInvoiceableList :
            CommonInvoiceableList;
    } else {
        Items = invoice.type === 'freight' ?
            FreightInvoiceableTable :
            CommonInvoiceableTable;
    }

    const title = isMobile ?
        ' №' + invoice.number :
        ' ' + t('invoice_no_for', {number: invoice.number, payer: invoice.payer.name});

    return (
        <Card sx={{my: isMobile ? undefined : 2}}>
            <CardHeader
                title={t('invoice') + title}
                action={<InvoiceRenderLink invoice={invoice}/>}
            />
            <CardContent>
                <Grid container sx={{textAlign: 'center'}}>
                    {isMobile && <Grid item xs={12}>{invoice.payer.name}</Grid>}
                    <Grid item xs={6} sm>
                        {t(`invoice_${invoice.type}`)}
                        {invoice.type === 'freight' && (
                            <>
                                {' '}
                                <Link href={`/voyage/${invoice.voyage.id}/finances`}>{invoice.voyage.number}</Link>
                            </>
                        )}
                    </Grid>
                    <Grid item xs={6} sm>
                        <Box
                            component="span"
                            color={invoice.status === InvoiceStatus.Unpaid ? 'error.light' : undefined}
                        >
                            {t(`status_${invoice.status}`)}
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm>{t('issue_date')}: <Date date={invoice.createdAt}/></Grid>
                    <Grid item xs={12} sm>{t('due_date')}: <Date date={invoice.dueDate}/></Grid>
                </Grid>
            </CardContent>
            <TableContainer>
                <Items
                    items={invoice.invoiceables}
                />
            </TableContainer>

            <Grid container justifyContent="flex-end">
                <Grid item sm={4}>
                    <TableContainer>
                        <Table size="small">
                            <TableBody>
                                <TableRow>
                                    <TableCell sx={{fontWeight: 'bold'}}>{t('total_due')}:</TableCell>
                                    <TableCell align="right" sx={{fontWeight: 'bold'}}>
                                        <Currency value={invoice.total}/>
                                    </TableCell>
                                </TableRow>
                                {!!invoice.totalTax && (
                                    <TableRow>
                                        <TableCell sx={{fontWeight: 'bold'}}>{t('tax')}:</TableCell>
                                        <TableCell align="right" sx={{fontWeight: 'bold'}}>
                                            <Currency value={invoice.totalTax}/>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {!!invoice.prepaid && (
                                    <TableRow>
                                        <TableCell sx={{fontWeight: 'bold'}}>{t('advance_payment')}:</TableCell>
                                        <TableCell align="right" sx={{fontWeight: 'bold'}}>
                                            <Currency value={invoice.prepaid}/>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {(!!invoice.prepaid || !!invoice.totalTax) && (
                                    <TableRow>
                                        <TableCell sx={{fontWeight: 'bold'}}>{t('grand_total')}:</TableCell>
                                        <TableCell align="right" sx={{fontWeight: 'bold'}}>
                                            <Currency value={invoice.grandTotal}/>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>

            <Typography whiteSpace="pre-line">
                {invoice.note}
            </Typography>

            <TableContainer sx={{width: {sm: '50%'}}}>
                <Table size="small">
                    <TableBody>
                        <TableRow>
                            <TableCell colSpan={2} align="center">{t('bank_details')}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>{t('bank')}</TableCell>
                            <TableCell>{invoice.bank.name}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>{t('branch')}</TableCell>
                            <TableCell>{invoice.bank.branchName}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>{t('account_number')}</TableCell>
                            <TableCell>{invoice.bank.accountNumber}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>{t('account_name')}</TableCell>
                            <TableCell>{invoice.bank.accountHolder}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>SWIFT/BIC</TableCell>
                            <TableCell>{invoice.bank.swiftCode}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell className="top">{t('address')}</TableCell>
                            <TableCell>{invoice.bank.branchAddress}</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </Card>
    );
};


export type FreightInvoiceableTableProps = {
    items: InvoiceableModel[],
    add?: () => void,
    remove?: (i: number) => void,
    setVal?: (i: number, name: string, value: string|number) => void,
}

export const FreightInvoiceableTable = ({items, add, remove}: FreightInvoiceableTableProps) => {
    const {t} = useTranslation();

    let hasFreight = false;
    let hasDelivery = false;
    let hasService = false;
    let hasForklift = false;

    items.forEach(item => {
        if (item.meta?.freight)
            hasFreight = true;

        if (item.meta?.forklift)
            hasForklift = true;

        if (item.meta?.delivery)
            hasDelivery = true;

        if (item.meta?.service)
            hasService = true;
    });

    const hasRate = items[0].currency === 'JPY' && hasFreight;

    return (
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>{t('model')}</TableCell>
                    <TableCell>{t('chassis')}</TableCell>
                    {hasFreight && <TableCell align="right">{t('freight')}</TableCell>}
                    {hasRate && <TableCell align="right">{t('usd_rate')}</TableCell>}
                    {hasForklift && <TableCell align="right">{t('forklift')}</TableCell>}
                    {hasDelivery && <TableCell align="right">{t('delivery')}</TableCell>}
                    {hasService && <TableCell align="right">{t('service')}</TableCell>}
                    <TableCell align="right">{t('price')}</TableCell>
                    {remove && <TableCell sx={{width: '1px', whiteSpace: 'nowrap'}}></TableCell>}
                </TableRow>
            </TableHead>
            <TableBody>
                {items.map((item, i) => (
                    <TableRow key={item.id}>
                        <TableCell>{i+1}</TableCell>
                        <TableCell>{item.name}</TableCell>

                        <TableCell>
                            <Link href={`/car/${item.itemId}`}>{item.description}</Link>
                        </TableCell>

                        {hasFreight && (
                            <TableCell align="right">
                                <Currency
                                    value={item.meta!.freight + (item.meta!.vehicle_type || 0)}
                                    currency="usd"
                                />
                            </TableCell>
                        )}

                        {hasRate && (
                            <TableCell align="right">
                                <Currency value={item.meta!.rate} currency={item.currency}/>
                            </TableCell>
                        )}

                        {hasForklift && (
                            <TableCell align="right">
                                <Currency value={item.meta!.forklift} currency={item.currency}/>
                            </TableCell>
                        )}

                        {hasDelivery && (
                            <TableCell align="right">
                                <Currency value={item.meta!.delivery} currency={item.currency}/>
                            </TableCell>
                        )}

                        {hasService && (
                            <TableCell align="right">
                                <Currency value={item.meta!.service} currency={item.currency}/>
                            </TableCell>
                        )}

                        <TableCell align="right">
                            <Currency value={item.price} currency={item.currency}/>
                        </TableCell>

                        {remove && (
                            <TableCell>
                                <IconButton
                                    aria-label="delete"
                                    size="small"
                                    onClick={() => remove(i)}
                                >
                                    <Delete/>
                                </IconButton>
                            </TableCell>
                        )}
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    )
};

export const FreightInvoiceableList = ({items, add, remove}: FreightInvoiceableTableProps) => {
    const {t} = useTranslation();

    return (
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>{t('model')}</TableCell>
                    <TableCell align="right">{t('price')}</TableCell>
                    {remove && <TableCell sx={{width: '1px', whiteSpace: 'nowrap'}}></TableCell>}
                </TableRow>
            </TableHead>
            <TableBody>
                {items.map((item, i) => (
                    <TableRow key={item.id}>
                        <TableCell>{i+1}</TableCell>
                        <TableCell>
                            <strong>{item.name}</strong> <br/>
                            <Link href={`/car/${item.itemId}`}>{item.description}</Link>

                            {item.meta?.freight && (
                                <Box>
                                    {t('freight')}: <Currency
                                    value={item.meta?.freight + (item.meta?.vehicle_type || 0)}
                                    currency="usd"
                                />
                                </Box>
                            )}

                            {item.meta?.rate && (
                                <Box>
                                    {t('usd_rate')}: <Currency value={item.meta?.rate} currency={item.currency}/>
                                </Box>
                            )}

                            {item.meta?.forklift && (
                                <Box>
                                    {t('forklift')}: <Currency value={item.meta?.forklift} currency={item.currency}/>
                                </Box>
                            )}

                            {item.meta?.delivery && (
                                <Box>
                                    {t('delivery')}: <Currency value={item.meta?.delivery} currency={item.currency}/>
                                </Box>
                            )}

                            {item.meta?.service && (
                                <Box>
                                    {t('service')}: <Currency value={item.meta?.service} currency={item.currency}/>
                                </Box>
                            )}
                        </TableCell>

                        <TableCell align="right">
                            <Currency value={item.price} currency={item.currency}/>
                        </TableCell>

                        {remove && (
                            <TableCell>
                                <IconButton
                                    aria-label="delete"
                                    size="small"
                                    onClick={() => remove(i)}
                                >
                                    <Delete/>
                                </IconButton>
                            </TableCell>
                        )}
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    )
};


export type CommonInvoiceableTableProps = {
    items: any[],
    add?: () => void,
    remove?: (i: number) => void,
    setVal?: (i: number, name: string, value: string|number) => void,
}

export const CommonInvoiceableTable = ({items, add, remove}: CommonInvoiceableTableProps) => {
    const {t} = useTranslation();

    return (
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>{t('item_name')}</TableCell>
                    <TableCell>{t('description')}</TableCell>
                    <TableCell align="right">{t('quantity')}</TableCell>
                    <TableCell align="right">{t('price')}</TableCell>
                    <TableCell align="right">{t('tax')}</TableCell>
                    {remove && <TableCell sx={{width: '1px', whiteSpace: 'nowrap'}}></TableCell>}
                </TableRow>
            </TableHead>
            <TableBody>
                {items.map((item, i) => (
                    <TableRow key={item.id}>
                        <TableCell>{i+1}</TableCell>
                        <TableCell>{item.name}</TableCell>
                        <TableCell>{item.description}</TableCell>
                        <TableCell align="right">{item.quantity}</TableCell>

                        <TableCell align="right">
                            <Currency value={item.price} currency={item.currency}/>
                        </TableCell>

                        <TableCell align="right">
                            {item.tax > 0 && <Currency value={item.price * item.tax / 100} currency={item.currency}/>}
                        </TableCell>

                        {remove && (
                            <TableCell>
                                <IconButton
                                    aria-label="delete"
                                    size="small"
                                    onClick={() => remove(i)}
                                >
                                    <Delete/>
                                </IconButton>
                            </TableCell>
                        )}
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    )
};

export const CommonInvoiceableList = ({items, add, remove}: CommonInvoiceableTableProps) => {
    const {t} = useTranslation();

    return (
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>{t('item_name')}</TableCell>
                    <TableCell align="right">{t('price')}</TableCell>
                    {remove && <TableCell sx={{width: '1px', whiteSpace: 'nowrap'}}></TableCell>}
                </TableRow>
            </TableHead>
            <TableBody>
                {items.map((item, i) => (
                    <TableRow key={item.id}>
                        <TableCell>{i+1}</TableCell>
                        <TableCell>
                            <strong>{item.name} {item.quantity > 1 ? `x ${item.quantity}` : ''}</strong> <br/>
                            {item.description}
                        </TableCell>

                        <TableCell align="right">
                            <Currency value={item.price} currency={item.currency}/>
                        </TableCell>

                        {remove && (
                            <TableCell>
                                <IconButton
                                    aria-label="delete"
                                    size="small"
                                    onClick={() => remove(i)}
                                >
                                    <Delete/>
                                </IconButton>
                            </TableCell>
                        )}
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    )
};

export default InvoiceView;
